// ============================================================
// ProgressionPages — ce qui se passe, page par page.
// ------------------------------------------------------------
// Une lecture prend jusqu'à 108 secondes. Sur 22 pages, un écran qui ne
// montre rien pendant plusieurs minutes est indistinguable d'un écran
// en panne — et l'utilisateur recharge, ou repart.
//
// Les pages se remplissent INDÉPENDAMMENT, dans le désordre : quatre
// partent ensemble, la troisième finit parfois avant la première. C'est
// visible ici, et c'est rassurant : on voit que ça travaille.
//
// ── Une page en erreur n'arrête pas les autres ──
//
// Elle affiche SA cause et son bouton de reprise. C'est la raison d'être
// du découpage page par page : sur 22 pages, une seule illisible ne doit
// pas coûter les 21 autres.

import Badge from '../../ui/Badge.jsx';
import Button from '../../ui/Button.jsx';
import { useState } from 'react';
import DecoupePlan from './DecoupePlan.jsx';
import { formatEuroOuTiret } from '../../lib/format.js';
import { libellePage } from './libellePage.js';

/** Les états, en langage d'atelier. Aucun nom de code à l'écran. */
const ETATS = {
  en_attente: { tone: 'neutral', label: 'En attente' },
  lecture: { tone: 'neutral', label: 'Lecture en cours…' },
  lue: { tone: 'neutral', label: 'Lue' },
  estimation: { tone: 'neutral', label: 'Estimation en cours…' },
  chiffree: { tone: 'ok', label: 'Chiffrée' },
  a_verifier: { tone: 'warn', label: 'À vérifier' },
  ignoree: { tone: 'neutral', label: 'Pas un plan de pièce' },
  erreur: { tone: 'danger', label: 'Erreur' },
};

export function ProgressionPages({ pages = [], actif, enCours, onRelancer, decoupe = null }) {
  const [aDecouper, setADecouper] = useState(null);
  if (pages.length === 0) return null;

  const chiffrees = pages.filter((p) => p.etat === 'chiffree').length;
  const aVerifier = pages.filter((p) => p.etat === 'a_verifier').length;
  const erreurs = pages.filter((p) => p.etat === 'erreur');

  // ── Quand tout est traité, la LISTE n'apprend plus rien ──
  //
  // Elle existe pour montrer que ça travaille : sur 22 pages et deux
  // minutes d'attente, un écran muet est indistinguable d'un écran en
  // panne. Une fois tout lu, elle répète ce que la bande de pièces dit
  // déjà, et elle repousse le plan vers le bas.
  //
  // Elle se replie donc — sauf s'il reste une erreur à reprendre, parce
  // qu'un incident replié est un incident que personne ne traite.
  const termine = !actif && erreurs.length === 0;
  const liste = (
      <ul className="progression__liste">
        {pages.map((p) => {
          const e = ETATS[p.etat] ?? ETATS.en_attente;
          return (
            <li key={p.file_id} className="progression__page">
              <span className="progression__num" title={p.document ?? undefined}>
                {libellePage(p)}
                {/* Une zone DIT d'où elle vient : « page 1 » deux fois de
                    suite, sans rien d'autre, se lirait comme un doublon. */}
                {p.decoupe_libelle ? ` — ${p.decoupe_libelle}` : ''}
              </span>
              <Badge tone={e.tone}>{e.label}</Badge>
              {p.price_unit != null ? (
                <span className="num">{formatEuroOuTiret(p.price_unit)}</span>
              ) : null}
              {/* ── Le bouton « Ouvrir » a été retiré ──
                  Il faisait doublon avec la bande de pièces, qui est
                  juste en dessous, porte la même sélection et montre en
                  plus la référence, l'état et le prix. Deux chemins vers
                  la même action sur le même écran, dont un qui n'a que
                  le numéro de page à offrir : c'est du bruit pendant les
                  deux minutes où l'utilisateur regarde justement cet
                  écran pour savoir où ça en est. */}
              {/* La CAUSE, pas « erreur ». Une page qui échoue sans dire
                  pourquoi se reprend à l'aveugle. */}
              {p.detail ? <span className="field__hint">{p.detail}</span> : null}
              {decoupe && !['lecture', 'en_attente', 'estimation'].includes(p.etat) ? (
                <Button size="sm" variant="ghost" onClick={() => setADecouper(p.file_id)}>
                  {p.decoupe_de ? '✂️ Modifier la découpe' : '✂️ Découper'}
                </Button>
              ) : null}
            </li>
          );
        })}
      </ul>
  );

  // Tout est traité : une seule ligne discrète, et le détail replié. Ce
  // bloc existe pour montrer que ça TRAVAILLE — sur 22 pages et deux
  // minutes d'attente, un écran muet est indistinguable d'un écran en
  // panne. Une fois fini, il n'a plus rien à dire que la bande de pièces
  // ne dise mieux, et il repoussait le plan vers le bas.
  return (
    <div className={termine ? 'progression progression--fini' : 'progression'}>
      <div className="row row--between">
        <p className="caps">
          {actif
            ? `Traitement en cours — ${enCours} page(s) sur ${pages.length}`
            : `${chiffrees + aVerifier} page(s) traitée(s) sur ${pages.length}`}
        </p>
        {/* « Tout relire » ne relisait RIEN : la relance ne reprend que les
            pages en échec, et il n'y en avait pas (6 octobre 2026). Le bouton
            n'existe donc que s'il a quelque chose à faire, et le dit. */}
        {erreurs.length > 0 ? (
          <Button size="sm" variant="secondary" onClick={onRelancer}>
            {`Reprendre ${erreurs.length} page(s) en échec`}
          </Button>
        ) : null}
      </div>

      {aDecouper ? (
        <DecoupePlan devisId={decoupe.devisId} pageId={aDecouper} avant={decoupe.avant}
          apres={decoupe.apres} onClose={() => setADecouper(null)} />
      ) : null}

      {termine ? (
        <details className="repli">
          <summary>Le détail page par page</summary>
          {liste}
        </details>
      ) : (
        liste
      )}
    </div>
  );
}

export default ProgressionPages;
